import React, { useEffect, useState, useMemo } from "react";
import { Add, Attachment, Remove } from "@mui/icons-material";
import OperationsHub from "../OperationsHub";
import InteractivePane from "../InteractivePane";
import ContentHeader from "../ContentHeader";
import NoContent from "../NoContent";
import Loading from "../Loading";
import Modal from "../Modal";
import List from "../List";
import StateMachineDiagram from "./StateMachineDiagram";
import Avatar from "../shared/Avatar.jsx";

import data from "../../../../_data/operations-hub.json";
import { formatProjectDate } from "../../date_utils";

export default function EngagementStateMachine() {
  const [tableData, setTableData] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let visibleProjects = [];

    if (data && data.projects) {
      visibleProjects = data.projects.filter((p) => p.isVisible);
    }

    setTableData(visibleProjects);
  }, []);

  return (
    <>
      <OperationsHub />

      {!tableData ? (
        <Loading />
      ) : tableData.length < 1 ? (
        <NoContent message={"No projects found."} />
      ) : (
        <>
          <ContentHeader
            title={"Engagement State Machine"}
            caption={
              "One simple state model to drive reporting, handoffs, and automation. Keep delivery predictable while moving fast."
            }
          />

          <section className="operations-hub">
            <List
              data={tableData}
              itemsPerPage={3}
              listFilterKey={"status"}
              listFilterVals={[
                "IN PROGRESS",
                "NEW",
                "WAITING FOR FEEDBACK",
                "COMPLETED",
                "ON HOLD",
                "DUPLICATE",
              ]}
              advancedFilterKeys={["subBU", "priority"]}
              dateFilterKey={"initiatedOn"}
              itemNameKey={"title"}
              itemCategoryKeys={["engagement", "subBU"]}
              itemFields={[
                {
                  key: "engagement",
                  label: "Engagement",
                  type: "chip",
                },
                {
                  key: "workType",
                  label: "Work Type",
                  type: "chip",
                },
                {
                  key: "dueOn",
                  label: "Due On",
                  type: "string",
                },
              ]}
              contributors={{
                key: "assignee",
                label: "Assignees",
              }}
              onItemClick={(project) => setSelected(project)}
            />
          </section>

          {selected && (
            <Modal onClose={() => setSelected(null)}>
              <ModalContent project={selected} />
            </Modal>
          )}
        </>
      )}
    </>
  );
}

function ModalContent({ project }) {
  const [active, setActive] = useState(null);

  const accordionItems = new Map([
    [
      "state-machine",
      {
        label: "State Machine",
        element: (
          <InteractivePane>
            <StateMachineDiagram project={project} />
          </InteractivePane>
        ),
      },
    ],
    [
      "classification",
      {
        label: "Classification",
        element: (
          <Classification
            engagement={project.engagement}
            subBU={project.subBU}
            workType={project.workType}
            stage={project.stage}
          />
        ),
      },
    ],
    [
      "timeline",
      {
        label: "Timeline",
        element: <Timeline project={project} />,
      },
    ],
    [
      "attachments",
      {
        label: "Attachments",
        element: <Attachments project={project} />,
      },
    ],
    [
      "technical-information",
      {
        label: "Technical Information",
        element: <TechnicalInformation project={project} />,
      },
    ],
  ]);

  const priorityLevelStyle = (level) => {
    switch (level) {
      case "Low":
        return "green";
      case "Medium":
        return "orange";
      case "High":
        return "darkred";
    }
  };

  const projectStatusStyle = (status) => {
    switch (status) {
      case "IN PROGRESS":
        return {
          backgroundColor: "#f7d827",
          boxShadow: "0px 0px 2px #a8910b",
        };
      case "NEW":
        return {
          backgroundColor: "#231ac9",
          boxShadow: "0px 0px 2px #241f8f",
        };
      case "WAITING FOR FEEDBACK":
        return {
          backgroundColor: "#f1ab12",
          boxShadow: "0px 0px 2px #bb8c28",
        };
      case "COMPLETED":
        return {
          backgroundColor: "#188d0e",
          boxShadow: "0px 0px 2px #2bd31c",
        };
      case "ON HOLD":
        return {
          backgroundColor: "#e23822",
          boxShadow: "0px 0px 2px #ad3525",
        };
      case "DUPLICATE":
        return {
          backgroundColor: "#8f2380",
          boxShadow: "0px 0px 2px #b91ca4",
        };
    }
  };

  const assignees = useMemo(() => project.assignee.map((c) => <Avatar key={c} name={c} />), []);

  const accordionEl = Array.from(accordionItems.keys()).map((k) => {
    return (
      <li className="accordionItem" key={k}>
        <button
          onClick={() => {
            if (!active || active?.label != accordionItems.get(k).label) {
              setActive(accordionItems.get(k));
            } else {
              setActive(null);
            }
          }}
        >
          <h3>{accordionItems.get(k).label}</h3>
          {active && active?.label === accordionItems.get(k).label ? <Remove /> : <Add />}
        </button>
        {active && active?.label === accordionItems.get(k).label && (
          <div style={{ padding: "1rem" }}>{active.element}</div>
        )}
        <hr />
      </li>
    );
  });

  return (
    <div className="modal-content">
      <h1>{project.title}</h1>
      <span style={{ display: "flex" }}>
        <p style={{ borderRight: "1px solid lightgray", paddingRight: "1rem" }}>#{project.id}</p>
        <span style={{ display: "flex", alignItems: "center", gap: "0.5rem", paddingLeft: "1rem" }}>
          <div className="project-status-circ" style={projectStatusStyle(project.status)} />
          <p>{project.status}</p>
        </span>
      </span>

      <div className="project-info">
        <span className="assignee-span">
          {assignees}
          <h3>Assignees</h3>
        </span>
        <span
          style={{
            fontSize: "small",
          }}
        >
          <h3>Priority</h3>
          <p
            style={{
              margin: "0px",
              padding: "0px",
              fontWeight: 500,
              color: priorityLevelStyle(project.priority),
            }}
          >
            {project.priority}
          </p>
        </span>
        <span
          style={{
            height: "fit-content",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            fontSize: "small",
          }}
        >
          <h3>Category:</h3>
          <p className="category-chip">{project.workType}</p>
        </span>
      </div>

      {/* ACCORDION SECTION */}
      <ul className="accordion">{accordionEl}</ul>
    </div>
  );
}

function Classification({ engagement, subBU, workType, stage }) {
  return (
    <div className="classification">
      <span>
        <h4>Engagement</h4>
        <p>{engagement}</p>
      </span>
      <span>
        <h4>Sub BU</h4>
        <p>{subBU}</p>
      </span>
      <span>
        <h4>Work Type</h4>
        <p>{workType}</p>
      </span>
      <span>
        <h4>Stage</h4>
        <p>Stage 1</p>
      </span>
    </div>
  );
}

function Timeline({ project }) {
  return (
    <div className="timeline">
      <span>
        <p>Created On</p>
        <div />
        <p>{formatProjectDate(project.initiatedOn) ?? "Unknown"}</p>
      </span>
      <span>
        <p>Last Updated</p>
        <div />
        <p>{formatProjectDate(project.initiatedOn) ?? "Unknown"}</p>
      </span>
      <span>
        <p>Due On</p>
        <div />
        <p>
          {project.dueOn != "" && project.dueOn ? formatProjectDate(project.dueOn) : "No due date"}
        </p>
      </span>
      <span>
        <p>Completed On</p>
        <div />
        <p>
          {project.completedOn != "" && project.completedOn
            ? formatProjectDate(project.completedOn)
            : "Not completed yet"}
        </p>
      </span>
    </div>
  );
}

function Attachments({ project }) {
  const el = [];

  if (!project.attachments || project.attachments < 1) {
    el.push(
      <li className="attachment-item" key="none">
        <p>No attachments available</p>
      </li>,
    );
  }

  for (let i = 0; i < project.attachments; i++) {
    el.push(
      <li className="attachment-item" key={i}>
        <Attachment fontSize="small" htmlColor="#5236AB" padding="0px" margin="0px" />
        <p>Attachment {i + 1}</p>
      </li>,
    );
  }

  return (
    <div className="attachments">
      <ul>{el}</ul>
    </div>
  );
}

function TechnicalInformation({ project }) {
  const techStack = project.primaryTechStack.map((tech) => (
    <li className="tech-chip" key={tech}>
      <p>{tech}</p>
    </li>
  ));

  const technology = project.technology.map((tech) => (
    <li className="tech-chip" key={tech}>
      <p>{tech}</p>
    </li>
  ));

  return (
    <div className="technical-information">
      <h4>Primary Tech Stacks</h4>
      <ul>
        {!techStack || techStack.length < 1 ? (
          <li className="tech-chip">
            <p>N/A</p>
          </li>
        ) : (
          techStack
        )}
      </ul>

      <h4>Technology</h4>
      <ul>
        {!technology || technology.length < 1 ? (
          <li className="tech-chip">
            <p>N/A</p>
          </li>
        ) : (
          technology
        )}
      </ul>

      <h4>Estimates</h4>
      <p>This is an example of estimates</p>
    </div>
  );
}
